import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ClaraTrendChart } from "./ClaraTrendChart"
import { useDialogFocus } from "./dialogFocus"
import styles from "./Clara.module.css"

/* Enlarge a Clara chart so it can be read.

   From the NSICU, via Nitya (24 Sep): "graphs and data get generated and are very hard
   to read on a screen due to the chat only using maybe a third of the window for the
   graph." The enlarged view carries the axes.

   Saving the chart as an image and downloading its data as CSV were built and then
   removed: both take patient data off the platform, which is a PHI decision to make
   first, not a UI one. They are in git history (commit 12db09845) if that is agreed. */

export function ChartActions({ chartProps, charts = [], label }) {
	const [open, setOpen] = useState(false)
	const sheetRef = useRef(null)
	useDialogFocus(open, sheetRef)

	useEffect(() => {
		if (!open) {
			return undefined
		}
		const onKey = event => event.key === "Escape" && setOpen(false)
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [open])

	if (!charts.length && (!chartProps?.trend || chartProps.trend.length < 2)) {
		return null
	}

	return (
		<>
			<div className={styles.chartActions}>
				<button type="button" className={styles.chartAction} onClick={() => setOpen(true)} aria-haspopup="dialog">
					<svg className={styles.chartActionIcon} viewBox="0 0 24 24" aria-hidden="true">
						<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
					</svg>
					Enlarge chart
				</button>
			</div>
			{/* Rendered into the body: the chat column is a stacking context, and a fixed
			    overlay inside one cannot cover the header and sidebar outside it. */}
			{open && createPortal(
				<div className={styles.chartOverlay} onClick={() => setOpen(false)} role="presentation">
					<div ref={sheetRef} className={styles.chartSheet} onClick={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Enlarged chart: ${label}`} tabIndex={-1}>
						<div className={styles.chartSheetHead}>
							<span>{label}</span>
							<button type="button" className={styles.chartAction} onClick={() => setOpen(false)} aria-label="Close enlarged chart">Close</button>
						</div>
						{charts.length ? charts.map(chart => (
							<div key={chart.label}>
								<div className={styles.chartSeriesName}>{chart.label}</div>
								<ClaraTrendChart {...chart.chartProps} height={Math.max(220, Math.round(window.innerHeight * 0.62 / charts.length))} />
							</div>
						)) : <ClaraTrendChart {...chartProps} height={Math.max(360, Math.round(window.innerHeight * 0.62))} />}
					</div>
				</div>,
				document.body,
			)}
		</>
	)
}
